<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";
import { useAccountSecuritySettings } from "@/composables/Security/useAccountSecuritySettings";
import { useCommoditySecuritySettings } from "@/composables/Security/useCommoditySecuritySettings";
import { useAuthStore } from "@/stores/auth";
import { useParametersStore } from "@/stores/parameters";
import { usePortalSecuritySettings } from "@/composables/Security/usePortalSecuritySettings";
import { useMiscSecuritySettings } from "@/composables/Security/useMiscSecuritySettings";
import ContractsSecuritySettings from "@/helpers/Security/ContractsSecuritySettings";
import PaymentsSecuritySettings from "@/helpers/Security/PaymentsSecuritySettings";
import PurchasesSecuritySettings from "@/helpers/Security/PurchasesSecuritySettings";
import OrdersSecuritySettings from "@/helpers/Security/OrdersSecuritySettings";
import OilEnergySecuritySettings from "@/helpers/Security/OilEnergySecuritySettings";

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: boolean];
}>();

interface DrawerResourceLink {
  title: string;
  icon?: string;
  to?: string;
  href?: string;
  // Names the click on a link with no route, which is otherwise named by its route.
  trackName?: string;
  target?: string;
  visible: boolean;
}

interface DrawerMenuGroup {
  key: string;
  title: string;
  icon: string;
  links: DrawerResourceLink[];
}

const authStore = useAuthStore();
const parametersStore = useParametersStore();
const accountSecuritySettings = useAccountSecuritySettings();
const commoditySecuritySettings = useCommoditySecuritySettings();
const securitySettings = usePortalSecuritySettings();
const miscSecuritySettings = useMiscSecuritySettings();
const contractsSecuritySettings = ContractsSecuritySettings.getInstance();
const paymentsSecuritySettings = PaymentsSecuritySettings.getInstance();
const purchasesSecuritySettings = PurchasesSecuritySettings.getInstance();
const ordersSecuritySettings = OrdersSecuritySettings.getInstance();
const oilEnergySecuritySettings = OilEnergySecuritySettings.getInstance();
const { smAndUp, xs } = useDisplay();
const route = useRoute();

const isInstalledAsPwa = window.matchMedia("(display-mode: standalone)").matches;
const drawerState = ref(props.modelValue);

watch(
  () => props.modelValue,
  (value) => {
    drawerState.value = value;
  }
);

watch(drawerState, (value) => {
  emit("update:modelValue", value);
});

watch(
  smAndUp,
  (value) => {
    if (value && authStore.isAuthenticated) {
      drawerState.value = true;
    }
  },
  { immediate: true }
);

const drawerLinks = computed(() => {
  return [
    {
      title: "Home",
      icon: "mdi-home",
      to: "/home",
      visible: authStore.isAuthenticated,
    },
  ].filter((link) => link.visible);
});

const accountLinks = computed(() => {
  if (!accountSecuritySettings.showAccountMenu.value) {
    return [];
  }

  return [
    {
      title: "Summary",
      to: "/summary/view",
      visible: accountSecuritySettings.showAccountSummaryPage.value,
    },
    {
      title: "Address Book",
      to: "/addresses/view",
      visible: accountSecuritySettings.showAddressesPage.value,
    },
    {
      title: "Buyers",
      to: "/account/buyers",
      visible: accountSecuritySettings.showBuyersPage.value,
    },
    {
      title: "Profile",
      to: "/profile/view",
      visible: accountSecuritySettings.showProfilePage.value,
    },
    {
      title: "Login History",
      to: "/logins/view",
      visible: accountSecuritySettings.showLoginHistoryPage.value,
    },
    {
      title: "Patronage",
      to: "/patronage/view",
      visible: accountSecuritySettings.showPatronagePage.value,
    },
  ].filter((link) => link.visible);
});

const purchasesLinks = computed(() => {
  return [
    {
      title: "Credit Activity",
      to: "/credit-activity/view",
      visible: purchasesSecuritySettings.showViewCreditActivityPage,
    },
    {
      title: "Purchases",
      to: "/purchases/view",
      visible: purchasesSecuritySettings.showViewPurchasesPage,
    },
    {
      title: "Transactions",
      to: "/transactions/view",
      visible: purchasesSecuritySettings.showViewTransactionsPage,
    },
    {
      title: "Statements",
      to: "/statements/view",
      visible: purchasesSecuritySettings.showViewStatementsPage,
    },
    {
      title: "Purchase Summary",
      to: "/purchase-summary/view",
      visible: purchasesSecuritySettings.showViewPurchaseSummaryPage,
    },
    {
      title: "Fuel Purchase Summary",
      to: "/fuelPurchaseSummary/view",
      visible: purchasesSecuritySettings.showViewFuelPurchaseSummaryPage,
    },
    {
      title: "Upcoming Deliveries",
      to: "/upcomingDeliveries/view",
      visible: purchasesSecuritySettings.showViewUpcomingDeliveriesPage,
    },
  ].filter((link) => link.visible);
});

/**
 * Legacy shows these six in this order and the security positions were allocated to match, so the order is
 * load bearing rather than cosmetic. The group hides itself when the customer is not flagged as Oil & Energy
 * in Merchant, which OilEnergySecuritySettings folds into every page's own right.
 */
const oilEnergyLinks = computed(() => {
  if (!oilEnergySecuritySettings.showOilEnergyMenu) {
    return [];
  }

  return [
    {
      title: "Tank Information",
      to: "/oilEnergy/tanks",
      visible: oilEnergySecuritySettings.showTanksPage,
    },
    {
      title: "Budget Billing",
      to: "/oilEnergy/budgetBilling",
      visible: oilEnergySecuritySettings.showBudgetBillingPage,
    },
    {
      title: "Delivery",
      to: "/oilEnergy/delivery",
      visible: oilEnergySecuritySettings.showDeliveryPage,
    },
    {
      title: "Energy Assistance",
      to: "/oilEnergy/energyAssistance",
      visible: oilEnergySecuritySettings.showEnergyAssistancePage,
    },
    {
      title: "Lease Agreements",
      to: "/oilEnergy/leaseAgreements",
      visible: oilEnergySecuritySettings.showLeaseAgreementsPage,
    },
    {
      title: "Sales Contracts",
      to: "/oilEnergy/salesContracts",
      visible: oilEnergySecuritySettings.showSalesContractsPage,
    },
  ].filter((link) => link.visible);
});

const contractsLinks = computed(() => {
  return [
    {
      title: "Bookings",
      to: "/bookings/view",
      visible: contractsSecuritySettings.showViewBookingsPage,
    },
    {
      title: "Prepaids",
      to: "/prepaids/view",
      visible: contractsSecuritySettings.showViewPrepaidsPage,
    },
  ].filter((link) => link.visible);
});

const managerLinks = computed(() => {
  return [
    {
      title: "Sales Overview",
      to: "/manager/salesOverview",
      visible: securitySettings.managerAccountSelected.value,
    },
  ].filter((link) => link.visible);
});

const ordersLinks = computed(() => {
  return [
    {
      title: "Create an Order",
      to: "/orders/create",
      visible: ordersSecuritySettings.showCreateOrderPage,
    },
    {
      title: "Orders",
      to: "/orders/view",
      visible: ordersSecuritySettings.showViewOrdersPage,
    },
  ].filter((link) => link.visible);
});

const paymentsLinks = computed(() => {
  return [
    {
      title: "Make a Payment",
      to: "/payment/create",
      visible: paymentsSecuritySettings.showCreatePaymentPage,
    },
    {
      title: "Automatic Payments",
      to: "/payment/automaticPayments",
      visible: paymentsSecuritySettings.showAutoPaymentsPage,
    },
    {
      title: "Pending Payments",
      to: "/payment/pending",
      visible: paymentsSecuritySettings.showPendingPaymentsPage,
    },
    {
      title: "Payment Sources",
      to: "/payment/sources",
      visible: paymentsSecuritySettings.showPaymentSourcesPage,
    },
    {
      title: "Payment History",
      to: "/payment/history",
      visible: paymentsSecuritySettings.showPaymentHistoryPage,
    },
  ].filter((link) => link.visible);
});

const commodityLinks = computed(() => {
  if (!commoditySecuritySettings.showCommodityMenu.value) {
    return [];
  }

  return [
    {
      title: "Cash Bids",
      to: "/bids/view",
      visible: commoditySecuritySettings.showCashBidsPage.value,
    },
    {
      title: "Balances",
      to: "/balances/view",
      visible: commoditySecuritySettings.showBalancesPage.value,
    },
    {
      title: "Activity",
      to: "/activity/view",
      visible: commoditySecuritySettings.showActivityPage.value,
    },
    {
      title: "Offers",
      to: "/offers/view",
      visible: commoditySecuritySettings.showOffersPage.value,
    },
    {
      title: "Contracts",
      to: "/contracts/view",
      visible: commoditySecuritySettings.showContractsPage.value,
    },
    {
      title: "Contracts Needing to be Signed",
      to: "/unsigned-contracts/view",
      visible: commoditySecuritySettings.showContractsPage.value,
    },
    {
      title: "Tickets",
      to: "/tickets/view",
      visible: commoditySecuritySettings.showTicketsPage.value,
    },
    {
      title: "Grain Bank",
      to: "/grain-bank/view",
      visible: commoditySecuritySettings.showGrainBankPage.value,
    },
    {
      title: "Proof of Yield",
      to: "/proof-of-yield/view",
      visible: commoditySecuritySettings.showProofOfYieldPage.value,
    },
  ].filter((link) => link.visible);
});

const fieldAlyticsDisplayName = computed(() => {
  return (
    parametersStore.parameterSettings.fieldAlyticsDisplayName?.trim() ||
    "FieldAlytics"
  );
});

const miscLinks = computed<DrawerResourceLink[]>(() => {
  return [
    {
      title: "Contact Us",
      to: "/contact",
      visible: miscSecuritySettings.showContactUsPage.value,
    },
    {
      title: "Licenses",
      to: "/licenses",
      visible: miscSecuritySettings.showLicensesPage.value,
    },
    {
      title: fieldAlyticsDisplayName.value,
      to: "/fieldalytics",
      visible: miscSecuritySettings.showFieldAlyticsPage.value,
    },
    {
      title: "Login to CFS",
      href: parametersStore.parameterSettings.cfsPortalUrl,
      trackName: "loginToCfs",
      visible:
        parametersStore.parameterSettings.useCfs &&
        !!parametersStore.parameterSettings.cfsPortalUrl &&
        !securitySettings.managerAccountSelected.value,
    },
  ].filter((link) => link.visible);
});

/**
 * Only worth offering on a phone, and only to someone who has not already added it. vue-mq's `sm` breakpoint
 * was everything under 600px, which is exactly Vuetify's `xs`, so the same growers are offered it as before.
 */
const showInstallAppButton = computed(() => xs.value && !isInstalledAsPwa);

/**
 * Each section collapses, which is how the portal behaved before the Vue 3 upgrade turned these into plain
 * subheaders that cannot be clicked. The icons are the ones each section carried then, so a grower coming from
 * the old portal sees the same shape. A section with nothing visible in it is left out rather than shown empty.
 */
const menuGroups = computed<DrawerMenuGroup[]>(() => {
  return [
    { key: "account", title: "Account", icon: "mdi-account", links: accountLinks.value },
    { key: "purchases", title: "Purchases", icon: "mdi-package-variant", links: purchasesLinks.value },
    { key: "oilEnergy", title: "Oil & Energy", icon: "mdi-propane-tank-outline", links: oilEnergyLinks.value },
    { key: "contracts", title: "Contracts", icon: "mdi-file-document-edit", links: contractsLinks.value },
    { key: "manager", title: "Manager", icon: "mdi-account-supervisor", links: managerLinks.value },
    { key: "orders", title: "Orders", icon: "mdi-cart", links: ordersLinks.value },
    { key: "payments", title: "Payments", icon: "mdi-credit-card-outline", links: paymentsLinks.value },
    { key: "commodity", title: "Commodity", icon: "mdi-barley", links: commodityLinks.value },
    { key: "resources", title: "Resources", icon: "mdi-dots-horizontal", links: miscLinks.value },
  ].filter((group) => group.links.length);
});

const openGroups = ref<string[]>([]);

/**
 * Opens the section the grower is actually in. Without this a refresh, a bookmark or a redirect lands them on a
 * page whose section is shut, with no indication of where they are. Closing a section stays closed: only the
 * section matching the current page is ever forced open.
 */
watch(
  [() => route.path, menuGroups],
  () => {
    const current = menuGroups.value.find((group) =>
      group.links.some((link) => link.to && route.path.startsWith(link.to))
    );

    if (current && !openGroups.value.includes(current.key)) {
      openGroups.value = [...openGroups.value, current.key];
    }
  },
  { immediate: true }
);
</script>

<template>
  <v-navigation-drawer v-model="drawerState" app>
    <div class="pa-4">
      <v-img src="/img/main-logo.png" max-width="220" />
    </div>

    <v-list v-model:opened="openGroups" nav density="comfortable">
      <v-list-item
        v-for="link in drawerLinks"
        :key="link.title"
        v-track="`nav.${link.to}`"
        :prepend-icon="link.icon"
        :to="link.to"
      >
        <v-list-item-title>{{ link.title }}</v-list-item-title>
      </v-list-item>

      <v-list-group
        v-for="group in menuGroups"
        :key="group.key"
        :value="group.key"
      >
        <template #activator="{ props: activatorProps }">
          <v-list-item
            v-bind="activatorProps"
            :prepend-icon="group.icon"
            :title="group.title"
          />
        </template>

        <v-list-item
          v-for="link in group.links"
          :key="link.title"
          v-track="`nav.${link.to ?? link.trackName}`"
          :to="link.to"
          :href="link.href"
          :target="link.target"
        >
          <v-list-item-title>{{ link.title }}</v-list-item-title>
        </v-list-item>
      </v-list-group>
    </v-list>

    <!--
      Add to Home Screen is a button under the menu rather than an entry inside a section, which is where it sat
      before the Vue 3 upgrade. A grower on a phone has to be able to see it without first opening a section.
    -->
    <div v-if="showInstallAppButton" class="px-4 pt-2">
      <v-btn
        color="primary"
        variant="text"
        to="/install-app-how-to"
        target="_blank"
        append-icon="mdi-chevron-right"
        block
      >
        Install App
      </v-btn>
    </div>

    <div v-if="parametersStore.isAnyLoading">
      <v-skeleton-loader type="list-item-avatar, list-item@2" />
      <v-skeleton-loader type="list-item-avatar, list-item@3" />
      <v-skeleton-loader type="list-item-avatar, list-item" />
    </div>

    <template #append>
      <div class="pa-4 text-body-2">
        <div v-if="authStore.selectedAccount?.name" class="font-weight-medium">
          {{ authStore.selectedAccount.name }}
        </div>
        <div v-else-if="authStore.webLogin.webLoginUserName">
          {{ authStore.webLogin.webLoginUserName }}
        </div>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<style scoped>
/*
  A page name is how a grower finds the page, so it is never shortened to an ellipsis. Measured against a 256px
  drawer: an icon on an indented child leaves the name 111px and cuts five of them off, including "Fuel Purchase
  Summary"; without it the name gets 167px and only "Contracts Needing to be Signed" is too long, so that one is
  allowed to take a second line. Top level entries keep their icons - they are not indented and have the room.
*/
:deep(.v-list-group__items .v-list-item-title) {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}
</style>
